/**
 * JSX components for instruction composition.
 *
 * Components are organized into:
 * 1. Content blocks - reusable patterns for tool guidance, examples, errors
 * 2. Control flow - conditional rendering helpers
 *
 * For dynamic section tags, use JSX intrinsic `<tag-*>` elements directly
 * (e.g., `<tag-identity>`, `<tag-constraints>`). The reconciler handles these natively.
 */

import { encode } from '@toon-format/toon';
import type { ReactNode } from 'react';

import { formatExpr } from '#core/lib/expr/format.ts';
import {
  type ColumnDoc,
  type ColumnType,
  describeColumnDocType,
  describeColumnType,
  scalarSubfields,
  structPathType,
  unwrapNullable,
} from '#core/lib/expr/schema/types.ts';
import { displaySymbolForType } from '#core/lib/units/registry.ts';

/** Renders a value as pretty-printed JSON in a fenced code block. */
export function Json({ value }: { value: unknown }) {
  return <pre>{JSON.stringify(value, null, 2)}</pre>;
}
/**
 * Inline code carrying an s-expression. Rendered as `<code>` so the
 * reconciler emits a single backtick-wrapped span. Multi-line literals
 * keep their newlines inside the backticks — the LLM parses them fine
 * and we avoid the ~2-line-per-block overhead of fenced `<pre>` blocks
 * plus the "``` glued to step line" markdown-malformation bug (the
 * reconciler doesn't insert a leading newline before a `<pre>` sibling
 * inside an `<li>`).
 *
 * Children must be a single string — JSX expressions inside the
 * expression are not interpolated (it's source, not template).
 *
 * The component identity is the SSOT discriminator for the prompt
 * audit walker (`collectExprChildren` in
 * `#plugin-chat/test-utils/exprAudit`): only `<Expr>` children get
 * dispatched through the engine. Inline `<code>` snippets are
 * treated as prose. Use `<Expr>` for COMPLETE, runnable expressions;
 * use `<code>` for partial fragments mentioned in surrounding prose.
 */
export function Expr({ children }: { children: string }) {
  // Lisp-indent the example so the rendered prompt shows balanceable,
  // multi-line source — the layout the agent must mimic to author and
  // self-correct deep pipelines. Layout-only; the audit walker reads the
  // raw `children` prop, so evaluation is unaffected.
  return <code>{formatExpr(children)}</code>;
}

export function Toon({ value }: { value: unknown }) {
  return <pre>{encode(value)}</pre>;
}

interface HeaderedBulletListProps {
  /** Section header content (`<header>X</header>` renders as `**X:**`). */
  header: ReactNode;
  /** Bullet items. Returns null when empty/undefined so callers can pass
   *  optional rule arrays without conditional wrapping. Items may
   *  contain inline JSX (e.g. `<MyTool/>` tool references) — the
   *  reconciler resolves any registered tool elements to backticked
   *  wire names. */
  items: readonly ReactNode[] | undefined;
}

export function HeaderedBulletList({ header, items }: HeaderedBulletListProps) {
  if (!items || items.length === 0) {
    return null;
  }
  return (
    <>
      <header>{header}</header>
      <ul>
        {items.map((item: ReactNode, i: number) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: item order is stable per caller; items can be non-string ReactNode so item itself is not a usable key.
          <li key={i}>{item}</li>
        ))}
      </ul>
    </>
  );
}

interface ToolGuidanceProps {
  description?: string;
  whenToUse: readonly ReactNode[];
  whenNotToUse?: readonly ReactNode[];
  /** Additional tool-specific guidance rendered after the when-to/not-to sections. */
  children?: ReactNode;
}

/**
 * Reusable block for tool usage guidance with when-to-use/not-to-use patterns.
 * The section headers are generic ("When to use this tool") — no tool name is
 * read or rendered here.
 *
 * `children` render after the when-to/not-to sections and are intended for
 * tool-specific guidance that doesn't fit the structured when-to-use pattern
 * (procedures, parameter dictionaries, output notes, etc.).
 */
export function ToolGuidance({ description, whenToUse, whenNotToUse, children }: ToolGuidanceProps) {
  return (
    <>
      {description && <p>{description}</p>}
      <HeaderedBulletList header={<>When to use this tool</>} items={whenToUse} />
      <HeaderedBulletList header={<>When not to use this tool</>} items={whenNotToUse} />
      {children}
    </>
  );
}

interface QueryExampleProps {
  user: string;
  steps: readonly string[];
  name?: string;
}

/**
 * Reusable block for query workflow examples showing user request and assistant steps.
 */
export function QueryExample({ user, steps, name = 'query' }: QueryExampleProps) {
  const stepsText = steps.map((step) => `Asst: ${step}`).join('\n');
  return <example name={name}>{`User: "${user}"\n${stepsText}`}</example>;
}

interface ColumnSchemaTableProps {
  columns: readonly ColumnDoc[];
  caption?: ReactNode;
}

/**
 * Enumerate a column's directly-scalar struct members as dotted sub-paths
 * paired with their own leaf type, so each member can be surfaced as its own
 * schema row with its own unit. Returns an empty list for non-struct columns.
 */
const structSubRows = (
  name: string,
  type: ColumnType,
): readonly { readonly path: string; readonly type: ColumnType }[] => {
  const inner = unwrapNullable(type);
  if (inner.kind !== 'struct') {
    return [];
  }
  return scalarSubfields(inner).flatMap((field) => {
    const fieldType = structPathType(type, [field]);
    return fieldType === undefined ? [] : [{ path: `${name}.${field}`, type: fieldType }];
  });
};
/**
 * Render a `Column | Type | Unit | Description` markdown table from a list
 * of {@link ColumnDoc} entries. Used by tools that surface dataset /
 * view schemas in their LLM-facing result message.
 */
export function ColumnSchemaTable({ columns, caption }: ColumnSchemaTableProps) {
  if (!columns || columns.length === 0) {
    return null;
  }
  return (
    <>
      {caption}
      <table>
        <thead>
          <tr>
            <th>Column</th>
            <th>Type</th>
            <th>Unit</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {columns.flatMap((c) => [
            <tr key={c.name}>
              <td>{c.name}</td>
              <td>{describeColumnDocType(c)}</td>
              <td>{displaySymbolForType(c.type)}</td>
              <td>{c.description ?? ''}</td>
            </tr>,
            ...structSubRows(c.name, c.type).map(({ path, type }) => (
              <tr key={path}>
                <td>{path}</td>
                <td>{describeColumnType(type)}</td>
                <td>{displaySymbolForType(type)}</td>
                <td />
              </tr>
            )),
          ])}
        </tbody>
      </table>
    </>
  );
}
